import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export type LiteSiteCardAccent = 'default' | 'warning' | 'danger' | 'info';

const ACCENT_DOT: Record<LiteSiteCardAccent, string> = {
  default: 'bg-slate-300 dark:bg-slate-600',
  warning: 'bg-amber-500',
  danger: 'bg-danger-500',
  info: 'bg-sky-500',
};

type LiteSiteCardProps = {
  title: string;
  subtitle?: ReactNode;
  accent?: LiteSiteCardAccent;
  /** Right-aligned slot on the main row (status pill, Claim button, chevron). */
  pill?: ReactNode;
  /** Row navigation target — main row renders as a Link. */
  to?: string;
  /** Row click handler — main row renders as a button (ignored when `to` is set). */
  onClick?: () => void;
  /** Rendered below the main row: action button rows, day pickers, etc. */
  actions?: ReactNode;
  /** Extra lines inside the text column, below the subtitle. */
  children?: ReactNode;
};

export function LiteSiteCard({
  title,
  subtitle,
  accent = 'default',
  pill,
  to,
  onClick,
  actions,
  children,
}: LiteSiteCardProps) {
  const rowClass = 'flex items-center gap-3 min-w-0 flex-1 min-h-[44px]';

  const rowContent = (
    <>
      <span
        className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${ACCENT_DOT[accent]}`}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
          {title}
        </p>
        {subtitle ? (
          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
            {subtitle}
          </p>
        ) : null}
        {children}
      </div>
    </>
  );

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-3">
      <div className="flex items-center gap-3">
        {to ? (
          <Link to={to} className={rowClass}>
            {rowContent}
          </Link>
        ) : onClick ? (
          <button
            type="button"
            onClick={onClick}
            className={`${rowClass} text-left`}
          >
            {rowContent}
          </button>
        ) : (
          <div className={rowClass}>{rowContent}</div>
        )}
        {pill ? (
          <div className="flex items-center flex-shrink-0">{pill}</div>
        ) : null}
      </div>
      {actions ? <div className="mt-3">{actions}</div> : null}
    </div>
  );
}
